/**
 * Datenschutz-/Leistungs-Auswahl (Privacy-Mode eu_only vs us_ok) — TQ #8466.
 * DSGVO-Tier-Wahl pro Org. 'local' wird der Kundin NIE als Tier angeboten
 * (interner Routing-Wert). us_ok erfordert explizites Consent — fail-closed im
 * Backend (cortex PR #107: us_ok ohne privacy_confirmed -> 400).
 *
 * TQ #13061: Erweiterung um dritte Stufe 'nur_lokal' (Patric-Ratifikation 09.08.2026).
 * Monotone Leiter: lokal < EU < EU+US — Obergrenze nur verschärfen, nie aufweichen.
 *
 * TQ #8459/#10086: extrahiert aus AriaOrgSettings.tsx (dort als "Datenschutz"-
 * Settings-Tab), damit derselbe Selector auch im OnboardingWizard verwendbar
 * ist (Onboarding-Schritt fehlte komplett — #8459).
 */
import { useState, useEffect, type CSSProperties } from 'react'
import { useAuth } from '@it-sce/ui/contexts/KeycloakAuthContext'
import { api, type PrivacyModeInfo, type ProcessingPreference } from '../services/api'

function tierStyle(active: boolean, accent: string, bg: string, fg: string): CSSProperties {
  return {
    flex: '1 1 220px',
    textAlign: 'left',
    padding: '0.875rem 1rem',
    borderRadius: 10,
    border: `1.5px solid ${active ? accent : '#d1d5db'}`,
    background: active ? bg : 'white',
    color: active ? fg : '#374151',
    cursor: active ? 'default' : 'pointer',
    transition: 'all 0.15s',
  }
}

interface PrivacyModeSelectorProps {
  /** Nur Admin/Owner duerfen aendern (PUT ist serverseitig admin/owner-only). Default true. */
  canEdit?: boolean
  /** Aufgerufen nach erfolgreichem Speichern — z.B. um im Onboarding zum naechsten Schritt zu springen. */
  onSaved?: (info: PrivacyModeInfo) => void
}

export default function PrivacyModeSelector({ canEdit = true, onSaved }: PrivacyModeSelectorProps) {
  const { token } = useAuth()
  const [info, setInfo] = useState<PrivacyModeInfo | null>(null)
  const [saving, setSaving] = useState(false)
  const [consentDialog, setConsentDialog] = useState<string | false>(false)
  const [forbidden, setForbidden] = useState(false)
  const [error, setError] = useState<string | null>(null)
  // readOnly kombiniert den client-seitigen Rollen-Check (canEdit, normale
  // Mitglieder duerfen nur lesen) mit einem etwaigen echten 403 (Defense-in-
  // Depth, falls die Rolle serverseitig doch striktere Regeln hat). GET
  // /api/v1/privacy-mode selbst ist fuer alle Mitglieder erlaubt -- nur PUT
  // ist admin/owner-only (siehe api/privacy_mode.rs).
  const readOnly = forbidden || !canEdit

  useEffect(() => {
    if (!token) return
    api.setToken(token)
    api
      .getPrivacyMode()
      .then(setInfo)
      .catch((err: { error?: string }) => {
        const msg = String(err?.error ?? '')
        if (msg.includes('403') || msg.includes('Forbidden')) setForbidden(true)
        else setError('Datenschutz-Einstellung konnte nicht geladen werden.')
      })
  }, [token])

  const applyPreference = async (pref: ProcessingPreference) => {
    if (!token || saving) return
    setSaving(true)
    setError(null)
    try {
      api.setToken(token)
      const updated = await api.setPrivacyMode(null, false, pref)
      setInfo(updated)
      setConsentDialog(false)
      onSaved?.(updated)
    } catch (err) {
      setError((err as { error?: string })?.error ?? 'Speichern fehlgeschlagen.')
    } finally {
      setSaving(false)
    }
  }

  // Bestimme die aktuelle Stufe — processing_preference hat Vorrang,
  // Fallback auf privacy_mode für Bestandsdaten ohne neue Spalte.
  const pref = info?.processing_preference
  const isLocal = pref === 'nur_lokal'
  const isEu = pref === 'nur_souveraen_eu' || (!pref && info?.privacy_mode === 'eu_only')
  const isUs = pref === 'eu_plus_us_vertraglich' || (!pref && info?.privacy_mode === 'us_ok')

  if (!info && !forbidden && !error) {
    return (
      <section className="aria-org-section">
        <p className="aria-org-section-hint">Lade Datenschutz-Einstellung…</p>
      </section>
    )
  }

  return (
    <section className="aria-org-section">
      <h3>Wo werden meine Daten verarbeitet?</h3>
      <p className="aria-org-section-hint">
        Hier legen Sie fest, wo Aria die KI-Anfragen Ihrer Organisation verarbeiten darf.
        Je enger der Raum, desto höher der Schutz — je weiter, desto schneller und
        leistungsfähiger sind manche Funktionen.
        {readOnly && ' (Lesezugriff — Administrator-Rechte zum Ändern erforderlich.)'}
      </p>

      <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap', margin: '1rem 0' }}>
        {/* Stufe 1: Nur lokal */}
        <button
          onClick={() => !isLocal && !readOnly && void applyPreference('nur_lokal')}
          disabled={isLocal || saving || readOnly}
          style={tierStyle(!!isLocal, '#059669', '#ecfdf5', '#047857')}
        >
          <div style={{ fontWeight: 700 }}>🏠 Nur lokal</div>
          <div style={{ fontSize: '0.75rem', opacity: 0.85 }}>
            Keine Cloud · Höchster Schutz · Unsere eigenen Server
          </div>
        </button>
        {/* Stufe 2: Nur EU */}
        <button
          onClick={() => !isEu && !readOnly && void applyPreference('nur_souveraen_eu')}
          disabled={isEu || saving || readOnly}
          style={tierStyle(!!isEu, '#10b981', '#f0fdf4', '#059669')}
        >
          <div style={{ fontWeight: 700 }}>🛡️ Nur in der EU</div>
          <div style={{ fontSize: '0.75rem', opacity: 0.85 }}>
            EU-only · Daten verlassen die EU nicht
            <br />
            <span style={{ fontWeight: 600 }}>(empfohlen)</span>
          </div>
        </button>
        {/* Stufe 3: EU + US */}
        <button
          onClick={() => !isUs && !readOnly && setConsentDialog('eu_plus_us_vertraglich')}
          disabled={isUs || saving || readOnly}
          style={tierStyle(!!isUs, '#6366f1', '#eef2ff', '#4f46e5')}
        >
          <div style={{ fontWeight: 700 }}>⚡ Auch US-Anbieter</div>
          <div style={{ fontSize: '0.75rem', opacity: 0.85 }}>
            US erlaubt · Höchste Leistung · Vertraglich abgesichert
          </div>
        </button>
      </div>

      {isUs && info?.privacy_confirmed_at && (
        <div style={{ fontSize: '0.75rem', color: '#9ca3af' }}>
          US-Freigabe bestätigt am{' '}
          {new Date(info.privacy_confirmed_at).toLocaleDateString('de-DE', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric',
          })}
        </div>
      )}
      {error && (
        <div className="aria-org-section-hint" style={{ color: '#dc2626' }}>
          {error}
        </div>
      )}

      {consentDialog && (
        <div
          style={{
            marginTop: '1rem',
            background: '#fafafa',
            border: '1px solid #e5e7eb',
            borderRadius: 10,
            padding: '1rem 1.25rem',
          }}
        >
          <div
            style={{
              fontWeight: 600,
              fontSize: '0.9rem',
              color: '#111827',
              marginBottom: '0.5rem',
            }}
          >
            US-Anbieter freigeben — Datenverarbeitung in den USA
          </div>
          <p style={{ fontSize: '0.8125rem', color: '#374151', margin: '0 0 0.75rem', lineHeight: 1.5 }}>
            Für besonders anspruchsvolle Aufgaben dürfen auch leistungsstarke US-Anbieter
            genutzt werden. Diese verarbeiten Ihre Daten nur auf Basis vertraglicher
            Zusicherungen (Auftragsverarbeitung, kein Training mit Ihren Daten).
            <strong> Ihre Daten können dabei die EU verlassen</strong> — dafür ist Ihre
            ausdrückliche Zustimmung erforderlich. Sie können jederzeit auf „Nur in der EU"
            zurückwechseln.
          </p>
          <div style={{ display: 'flex', gap: '0.625rem' }}>
            <button
              onClick={() => void applyPreference('eu_plus_us_vertraglich')}
              disabled={saving}
              style={{
                padding: '0.45rem 1rem',
                borderRadius: 6,
                border: 'none',
                background: '#6366f1',
                color: 'white',
                fontWeight: 600,
                fontSize: '0.8125rem',
                cursor: saving ? 'default' : 'pointer',
                opacity: saving ? 0.6 : 1,
              }}
            >
              {saving ? 'Wird gespeichert…' : 'Bestätigen & Freigeben'}
            </button>
            <button
              onClick={() => setConsentDialog(false)}
              disabled={saving}
              style={{
                padding: '0.45rem 1rem',
                borderRadius: 6,
                border: '1px solid #d1d5db',
                background: 'white',
                color: '#374151',
                fontWeight: 500,
                fontSize: '0.8125rem',
                cursor: 'pointer',
              }}
            >
              Abbrechen
            </button>
          </div>
        </div>
      )}
    </section>
  )
}
